/**
 * Trunnion AI - WebSocket Type Definitions
 * Types for real-time WebSocket events and payloads.
 */

import type { ExecutionStatus } from './execution.types';
import type { ClassificationLevel } from './agent.types';

export enum WSEvent {
  // Connection
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  ERROR = 'error',
  AUTHENTICATE = 'authenticate',
  AUTHENTICATED = 'authenticated',

  // Execution events
  EXECUTION_STARTED = 'execution:started',
  EXECUTION_STEP_STARTED = 'execution:step:started',
  EXECUTION_STEP_COMPLETED = 'execution:step:completed',
  EXECUTION_STEP_FAILED = 'execution:step:failed',
  EXECUTION_COMPLETED = 'execution:completed',
  EXECUTION_FAILED = 'execution:failed',
  EXECUTION_CANCELLED = 'execution:cancelled',

  // Tool call events
  TOOL_CALL_STARTED = 'tool:call:started',
  TOOL_CALL_COMPLETED = 'tool:call:completed',
  TOOL_CALL_FAILED = 'tool:call:failed',
  TOOL_CALL_DENIED = 'tool:call:denied',

  // Reasoning events
  REASONING_TRACE = 'reasoning:trace',

  // Approval events
  APPROVAL_REQUESTED = 'approval:requested',
  APPROVAL_GRANTED = 'approval:granted',
  APPROVAL_DENIED = 'approval:denied',
  APPROVAL_EXPIRED = 'approval:expired',

  // Token events
  TOKEN_USAGE_UPDATE = 'token:usage:update',

  // System events
  SYSTEM_HEALTH = 'system:health',
  CLASSIFICATION_CHANGE = 'classification:change',

  // Subscription
  SUBSCRIBE_EXECUTION = 'subscribe:execution',
  UNSUBSCRIBE_EXECUTION = 'unsubscribe:execution',
}

export interface WSExecutionStartedPayload {
  executionId: string;
  workflowId: string;
  workflowName: string;
  classification: ClassificationLevel;
  triggeredBy: string;
  startedAt: string;
}

export interface WSStepUpdatePayload {
  executionId: string;
  stepId: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: ExecutionStatus;
  agentId?: string;
  agentName?: string;
  timestamp: string;
  output?: Record<string, unknown>;
  error?: string;
}

export interface WSToolCallPayload {
  executionId: string;
  stepId: string;
  toolCallId: string;
  toolId: string;
  toolName: string;
  status: 'started' | 'completed' | 'failed' | 'denied';
  parameters?: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
  timestamp: string;
}

export interface WSReasoningTracePayload {
  executionId: string;
  stepId: string;
  agentId: string;
  traceId: string;
  type: 'thought' | 'observation' | 'action' | 'plan' | 'reflection';
  content: string;
  timestamp: string;
}

export interface WSApprovalPayload {
  executionId: string;
  stepId: string;
  approvalId: string;
  description: string;
  requiredRoles: string[];
  expiresAt: string;
  status: 'requested' | 'granted' | 'denied' | 'expired';
  approvedBy?: string;
  timestamp: string;
}

export interface WSTokenUsagePayload {
  executionId: string;
  stepId: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
  cumulativeTokens: number;
  cumulativeCost: number;
}

export interface WSSystemHealthPayload {
  status: 'healthy' | 'degraded' | 'unhealthy';
  activeExecutions: number;
  queuedExecutions: number;
  connectedAgents: number;
  timestamp: string;
}

export interface WSClassificationChangePayload {
  level: ClassificationLevel;
  reason: string;
  timestamp: string;
}
